import React from "react";
import Navbar from "../../components/Navbar";
import { Check, X, ArrowRight, Zap, DollarSign, Gauge, RefreshCw } from "lucide-react";

const comparisonData = {
  chroma: {
    name: "Chroma",
    tagline: "Embedded AI-native open-source database",
    pros: [
      "Extremely easy to get started - pip install and go",
      "Perfect for prototyping and local development",
      "Open-source with active community",
      "Native Python integration with great DX",
      "Zero infrastructure for small projects"
    ],
    cons: [
      "Limited scalability for production workloads",
      "Fewer enterprise features compared to competitors",
      "Performance degrades with large datasets",
      "Basic monitoring and observability tools",
      "Smaller ecosystem and fewer integrations"
    ],
    pricing: {
      tier: "Free/Budget",
      description: "Free for self-hosted. Cloud offering in beta with expected low-cost pricing model.",
      note: "Best for: Startups, prototypes, and small-scale applications"
    },
    performance: {
      latency: "Good for <1M vectors locally",
      throughput: "Optimised for development workflow"
    }
  },
  pinecone: {
    name: "Pinecone",
    tagline: "Fully managed vector database for production",
    pros: [
      "Production-ready with enterprise reliability",
      "Excellent scalability to billions of vectors",
      "Strong performance optimization and auto-tuning",
      "Comprehensive monitoring and analytics",
      "24/7 support and proven SLAs"
    ],
    cons: [
      "Significantly higher cost at scale",
      "Vendor lock-in with proprietary infrastructure",
      "Overkill for small projects or experiments",
      "Less control over underlying infrastructure",
      "Regional availability limitations"
    ],
    pricing: {
      tier: "Premium",
      description: "Starts at $70/month for 100K vectors. Can reach thousands monthly for large deployments.",
      note: "Best for: Production applications with high traffic and scale requirements"
    },
    performance: {
      latency: "Sub-50ms at any scale",
      throughput: "Handles billions of vectors efficiently"
    }
  }
};

const migrationDetails = {
  difficulty: "Easy-Medium",
  timeEstimate: "1-3 hours for 1M vectors",
  considerations: [
    "Simple API translation (Chroma to Pinecone)",
    "Minimal schema changes required",
    "Collection to index mapping needed",
    "Embedding model consistency verification"
  ],
  howWeHelp: [
    "Automated API endpoint translation",
    "Collection structure to Pinecone index mapping",
    "Batch upload optimization for faster transfer",
    "Embedding dimension and distance metric validation",
    "Production readiness assessment and recommendations"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl p-8 border border-border mb-8 shadow-sm">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-bg-soft border border-border">
          {isPositive ? (
            <Check className="w-3 h-3 text-green-600" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-text-secondary text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-white rounded-xl p-8 border border-border shadow-sm">
    <h3 className="text-2xl font-bold text-text-primary mb-2">{data.name}</h3>
    <p className="text-text-secondary text-sm mb-6">{data.tagline}</p>

    <div className="space-y-6">
      <div>
        <h4 className="text-text-primary font-semibold mb-3 flex items-center gap-2">
          <Check className="text-green-600 w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive={true} />
      </div>

      <div>
        <h4 className="text-text-secondary font-semibold mb-3 flex items-center gap-2">
          <X className="text-red-500 w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} isPositive={false} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-text-primary">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-bg-soft text-text-secondary border border-border">
        {data.tier}
      </span>
    </div>
    <p className="text-text-secondary text-sm mb-3">{data.description}</p>
    <p className="text-text-muted text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-text-primary">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Query Latency:</span>
        <span className="text-text-primary font-medium">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Throughput:</span>
        <span className="text-text-primary font-medium">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function ChromaVsPinecone() {
  return (
    <div className="bg-bg min-h-screen px-6">
      <Navbar />

      <div className="max-w-6xl py-28 mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-text-primary mb-4">
            Chroma vs Pinecone: Comparison 2026
          </h1>
          <p className="text-text-secondary text-xl max-w-3xl mx-auto">
            Embedded simplicity vs cloud-native scale. Compare ease of use, cost efficiency,
            production readiness, and migration complexity for your AI projects.
          </p>
        </div>

        {/* Side-by-Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.chroma} />
          <DatabaseCard data={comparisonData.pinecone} />
        </div>

        {/* Pricing */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.chroma.pricing} name="Chroma" />
            <PricingCard data={comparisonData.pinecone.pricing} name="Pinecone" />
          </div>
          <div className="mt-8 p-6 bg-bg-soft border border-border rounded-lg">
            <p className="text-text-secondary text-sm">
              <strong className="text-text-primary">Cost Analysis:</strong> Chroma is essentially
              free for self-hosted deployments, making it perfect for development and small-scale
              production. Pinecone's pricing reflects its enterprise-grade infrastructure—expect
              10-50x higher costs but with guaranteed performance and reliability at any scale.
            </p>
          </div>
        </ComparisonSection>

        {/* Performance */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.chroma.performance} name="Chroma" />
            <PerformanceCard data={comparisonData.pinecone.performance} name="Pinecone" />
          </div>
          <div className="mt-8 p-6 bg-bg-soft border border-border rounded-lg">
            <p className="text-text-secondary text-sm">
              <strong className="text-text-primary">Performance Considerations:</strong> Performance
              largely depends on dataset size, query patterns, and deployment setup. Chroma performs
              well for development workflows and small to medium datasets (under 1M vectors). For
              large-scale, high-traffic production systems handling millions of queries per day,
              specialized distributed vector databases may be more suitable, depending on latency
              and scalability requirements.
            </p>
          </div>
        </ComparisonSection>

        {/* Migration */}
        <ComparisonSection title="Migration from Chroma to Pinecone" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Migration Complexity</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">Difficulty Level:</span>
                  <span className="text-text-primary font-semibold">{migrationDetails.difficulty}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">Estimated Time:</span>
                  <span className="text-text-primary text-sm">{migrationDetails.timeEstimate}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Key Considerations</h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-text-muted flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* How We Help */}
        <div className="mt-16 bg-primary rounded-2xl p-12 text-center border border-primary-soft">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              How Vector Migration Simplifies Your Switch
            </h2>
            <p className="text-white/70 mb-8">
              Moving from prototype to production? We handle the entire migration from Chroma
              to Pinecone, ensuring your embeddings and search quality remain intact.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 text-left">
              {migrationDetails.howWeHelp.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-primary-dark/60 rounded-lg p-4 border border-primary-soft">
                  <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final Recommendation */}
        <div className="mt-16 bg-white border border-border rounded-xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-text-primary mb-4">Which Should You Choose?</h2>
          <div className="space-y-4 text-text-secondary">
            <p>
              <strong className="text-text-primary">Choose Chroma if:</strong> You're prototyping,
              building MVPs, or running small-scale applications (under 1M vectors). Perfect for
              local development, research projects, and teams that want to avoid infrastructure
              complexity and costs during early stages.
            </p>
            <p>
              <strong className="text-text-primary">Choose Pinecone if:</strong> You're building
              production applications with scale requirements, need guaranteed performance SLAs,
              require enterprise support, or expect to handle millions of queries daily. Essential
              for customer-facing applications where reliability is critical.
            </p>
            <p className="text-sm text-text-muted italic">
              Many teams start with Chroma for rapid prototyping, then migrate to Pinecone when
              moving to production. This "prototype-to-production" path is common and well-supported
              by migration tools like ours.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}